import { useCallback, useEffect, useState } from 'react';

/**
 * Light/dark theme.
 *
 * Stored in localStorage — a UI preference, not a credential, so this is the
 * appropriate place for it. Defaults to the OS setting until the operator
 * chooses explicitly.
 */

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'insider.theme';

function systemTheme(): Theme {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStored(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  document.documentElement.style.colorScheme = theme;
}

/** Applied before React mounts so the first paint is already correct. */
export function initTheme(): void {
  applyTheme(readStored() ?? systemTheme());
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => readStored() ?? systemTheme());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((current) => {
      const next: Theme = current === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Private browsing can refuse storage; the theme still applies for this session.
      }
      return next;
    });
  }, []);

  return { theme, toggle };
}
